import { useState, useEffect, useRef } from 'react'
import { api } from '../lib/api'
import { useAuth } from '../hooks/useAuth'

const PERIODS = [
  { id:'today', label:'Today' },
  { id:'week',  label:'This Week' },
  { id:'month', label:'This Month' },
  { id:'all',   label:'All Time' },
]

const TABS = [
  { id:'overview',   label:'Overview',   icon:'📊' },
  { id:'funnel',     label:'Funnel',     icon:'⬡' },
  { id:'creatives',  label:'Creatives',  icon:'◐' },
  { id:'adcopy',     label:'Ad Copy',    icon:'◻' },
  { id:'scheduling', label:'Scheduling', icon:'◷' },
  { id:'winning',    label:'Winning',    icon:'◆' },
]

function filterByPeriod(rows, period) {
  if (period === 'all') return rows
  const now = new Date(), start = new Date()
  if (period === 'today') start.setHours(0,0,0,0)
  else if (period === 'week') { start.setDate(now.getDate()-now.getDay()); start.setHours(0,0,0,0) }
  else if (period === 'month') { start.setDate(1); start.setHours(0,0,0,0) }
  return rows.filter(r => new Date(r.updated_at||r.created_at) >= start)
}

function PieChart({ data, size=160 }) {
  const ref = useRef(null)
  const total = data.reduce((s,d)=>s+d.value,0)
  useEffect(()=>{
    const c = ref.current; if(!c||!total) return
    const ctx=c.getContext('2d'), cx=size/2, cy=size/2, r=size/2-6
    ctx.clearRect(0,0,size,size)
    let start=-Math.PI/2
    data.forEach(d=>{
      const slice=(d.value/total)*2*Math.PI
      ctx.beginPath(); ctx.moveTo(cx,cy); ctx.arc(cx,cy,r,start,start+slice)
      ctx.closePath(); ctx.fillStyle=d.color; ctx.fill()
      ctx.strokeStyle='#0B0F19'; ctx.lineWidth=2; ctx.stroke()
      start+=slice
    })
    ctx.beginPath(); ctx.arc(cx,cy,r*0.52,0,2*Math.PI)
    ctx.fillStyle='#111827'; ctx.fill()
  },[data,size,total])
  if(!total) return <div style={{width:size,height:size,borderRadius:'50%',background:'var(--bg3)',border:'2px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'center',color:'var(--text3)',fontSize:11}}>No data</div>
  return (
    <div style={{position:'relative',width:size,height:size,flexShrink:0}}>
      <canvas ref={ref} width={size} height={size}/>
      <div style={{position:'absolute',inset:0,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',pointerEvents:'none'}}>
        <div style={{fontSize:20,fontWeight:800,color:'var(--text)',fontFamily:'DM Mono,monospace'}}>{total}</div>
        <div style={{fontSize:9,color:'var(--text3)',fontFamily:'DM Mono,monospace',textTransform:'uppercase'}}>Total</div>
      </div>
    </div>
  )
}

function StatusBreakdown({ rows, statusKey, colors }) {
  const counts = {}
  rows.forEach(r => { const s=r[statusKey]||'None'; counts[s]=(counts[s]||0)+1 })
  const total = rows.length
  const pieData = Object.entries(counts).map(([label,value],i)=>({
    label, value, color: colors[label] || ['#3B82F6','#10B981','#8B5CF6','#F59E0B','#EF4444','#64748B'][i%6]
  })).sort((a,b)=>b.value-a.value)
  return (
    <div style={{display:'flex',gap:20,alignItems:'flex-start',flexWrap:'wrap'}}>
      <PieChart data={pieData} size={140}/>
      <div style={{flex:1,display:'flex',flexDirection:'column',gap:6,minWidth:160}}>
        {pieData.map(d=>(
          <div key={d.label} style={{display:'flex',alignItems:'center',gap:8}}>
            <div style={{width:8,height:8,borderRadius:2,background:d.color,flexShrink:0}}/>
            <span style={{flex:1,fontSize:11,color:'var(--text2)',fontFamily:'DM Mono,monospace'}}>{d.label}</span>
            <span style={{fontSize:11,fontWeight:700,color:'var(--text)',fontFamily:'DM Mono,monospace'}}>{d.value}</span>
            {total>0 && <span style={{fontSize:10,color:'var(--text3)',fontFamily:'DM Mono,monospace',width:32,textAlign:'right'}}>{Math.round(d.value/total*100)}%</span>}
          </div>
        ))}
      </div>
    </div>
  )
}

function Card({ title, value, sub, color, icon }) {
  return (
    <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:12,padding:'14px 16px'}}>
      <div style={{fontSize:18,marginBottom:4}}>{icon}</div>
      <div style={{fontSize:24,fontWeight:800,color:color||'var(--text)',fontFamily:'DM Mono,monospace'}}>{value}</div>
      <div style={{fontSize:10,color:'var(--text3)',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px',fontFamily:'DM Mono,monospace',marginTop:3}}>{title}</div>
      {sub && <div style={{fontSize:11,color:'var(--text3)',marginTop:2}}>{sub}</div>}
    </div>
  )
}

function SectionCard({ title, children }) {
  return (
    <div style={{background:'var(--bg2)',border:'1px solid var(--border)',borderRadius:12,padding:16,marginBottom:12}}>
      <div style={{fontSize:10,fontWeight:700,color:'var(--text2)',textTransform:'uppercase',letterSpacing:'0.8px',fontFamily:'DM Mono,monospace',marginBottom:12}}>{title}</div>
      {children}
    </div>
  )
}

function ByGroup({ rows, groupKey, label='Items' }) {
  const counts = {}
  rows.forEach(r => { const g=r[groupKey]||'Unknown'; counts[g]=(counts[g]||0)+1 })
  const sorted = Object.entries(counts).sort((a,b)=>b[1]-a[1])
  const max = sorted[0]?.[1]||1
  return (
    <div style={{display:'flex',flexDirection:'column',gap:6}}>
      {sorted.slice(0,10).map(([name,count])=>(
        <div key={name} style={{display:'flex',alignItems:'center',gap:10}}>
          <div style={{width:80,fontSize:11,color:'var(--text2)',fontFamily:'DM Mono,monospace',flexShrink:0,textOverflow:'ellipsis',overflow:'hidden',whiteSpace:'nowrap'}}>{name}</div>
          <div style={{flex:1,height:6,background:'var(--border)',borderRadius:3,overflow:'hidden'}}>
            <div style={{height:'100%',width:`${(count/max)*100}%`,background:'var(--accent)',borderRadius:3}}/>
          </div>
          <div style={{fontSize:11,fontWeight:700,color:'var(--text)',fontFamily:'DM Mono,monospace',width:24,textAlign:'right'}}>{count}</div>
        </div>
      ))}
      {sorted.length===0 && <div style={{fontSize:12,color:'var(--text3)'}}>No data</div>}
    </div>
  )
}

const FUNNEL_COLORS = { 'Ready':'#10B981','In Progress':'#3B82F6','Proofreading':'#8B5CF6','On Hold':'#F59E0B','Cancelled':'#EF4444','None':'#64748B' }
const CREATIVE_COLORS = { 'Ready':'#10B981','In Progress':'#3B82F6','On Hold':'#F59E0B','Cancelled':'#EF4444','None':'#64748B' }
const ADCOPY_COLORS = { 'Approved':'#10B981','Review':'#3B82F6','Draft':'#94A3B8','Rejected':'#EF4444','Active':'#F59E0B','Paused':'#64748B','None':'#64748B' }
const SCHED_COLORS = { 'Active':'#10B981','Scheduled':'#3B82F6','Completed':'#8B5CF6','Paused':'#F59E0B','Cancelled':'#EF4444','None':'#64748B' }

export default function Reports() {
  const { user } = useAuth()
  const isOwner = user?.role === 'owner'
  const [period, setPeriod] = useState('month')
  const [activeTab, setActiveTab] = useState('overview')
  const [marketFilter, setMarketFilter] = useState('all')
  const [data, setData] = useState({ funnel:[], creatives:[], adcopy:[], scheduling:[], winning:[], products:[] })
  const [loading, setLoading] = useState(true)

  useEffect(()=>{
    async function load() {
      setLoading(true)
      try {
        const market = isOwner ? null : user?.assigned_market
        const [funnel, creatives, adcopy, scheduling, winning, products] = await Promise.all([
          api.getSection('funnel', market).catch(()=>[]),
          api.getSection('creatives', market).catch(()=>[]),
          api.getSection('adcopy', market).catch(()=>[]),
          api.getSection('scheduling', market).catch(()=>[]),
          api.getSection('winning', market).catch(()=>[]),
          api.getProducts().catch(()=>[]),
        ])
        setData({ funnel, creatives, adcopy, scheduling, winning, products })
      } catch(err) { console.error(err) }
      setLoading(false)
    }
    load()
  },[isOwner, user])

  const allMarkets = ['all', ...new Set([
    ...data.funnel.map(r=>r.market),
    ...data.creatives.map(r=>r.market),
  ].filter(Boolean))]

  function filtered(arr) {
    const byMarket = marketFilter==='all' ? arr : arr.filter(r=>r.market===marketFilter)
    return filterByPeriod(byMarket, period)
  }

  const f = { funnel:filtered(data.funnel), creatives:filtered(data.creatives), adcopy:filtered(data.adcopy), scheduling:filtered(data.scheduling), winning:filtered(data.winning), products:data.products }

  if(loading) return (
    <div style={{padding:40,color:'var(--text2)',fontFamily:'DM Mono,monospace',fontSize:13,display:'flex',alignItems:'center',gap:12}}>
      <div style={{width:16,height:16,border:'2px solid var(--border2)',borderTopColor:'var(--accent)',borderRadius:'50%',animation:'spin 0.7s linear infinite'}}/>
      Loading reports...
    </div>
  )

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',overflow:'hidden'}}>
      {/* Toolbar */}
      <div className="toolbar">
        <div className="toolbar-left">
          <span className="toolbar-title">📊 Reports</span>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
          {isOwner && allMarkets.length>2 && (
            <select value={marketFilter} onChange={e=>setMarketFilter(e.target.value)}
              style={{background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:7,color:'var(--text)',padding:'6px 10px',fontSize:12,fontFamily:'DM Mono,monospace',outline:'none'}}>
              {allMarkets.map(m=><option key={m} value={m}>{m==='all'?'All Markets':m}</option>)}
            </select>
          )}
          <div style={{display:'flex',gap:2,background:'var(--bg)',borderRadius:8,padding:3,border:'1px solid var(--border)'}}>
            {PERIODS.map(p=>(
              <button key={p.id} onClick={()=>setPeriod(p.id)} style={{padding:'5px 12px',border:'none',borderRadius:6,cursor:'pointer',fontSize:11,fontWeight:600,background:period===p.id?'var(--bg3)':'transparent',color:period===p.id?'var(--text)':'var(--text3)',transition:'all 0.15s'}}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{display:'flex',gap:2,padding:'8px 16px 0',background:'var(--bg2)',borderBottom:'1px solid var(--border)',flexShrink:0,overflowX:'auto',scrollbarWidth:'none'}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setActiveTab(t.id)} style={{padding:'7px 14px',border:'none',borderRadius:'8px 8px 0 0',cursor:'pointer',fontSize:12,fontWeight:600,whiteSpace:'nowrap',background:activeTab===t.id?'var(--bg)':'transparent',color:activeTab===t.id?'var(--text)':'var(--text3)',borderBottom:activeTab===t.id?'2px solid var(--accent)':'2px solid transparent',transition:'all 0.15s'}}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{flex:1,overflowY:'auto',padding:20}}>

        {/* OVERVIEW */}
        {activeTab==='overview' && (
          <div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))',gap:10,marginBottom:16}}>
              <Card icon="📦" title="Products" value={f.products.length} color="var(--accent)"/>
              <Card icon="⬡" title="Funnels" value={f.funnel.length} color="#60A5FA" sub={`${f.funnel.filter(r=>r.funnel_status==='Ready').length} ready`}/>
              <Card icon="◐" title="Creatives" value={f.creatives.length} color="#A78BFA" sub={`${f.creatives.filter(r=>r.creative_status==='Ready').length} ready`}/>
              <Card icon="◻" title="Ad Copies" value={f.adcopy.length} color="#34D399" sub={`${f.adcopy.filter(r=>r.status==='Approved').length} approved`}/>
              <Card icon="◷" title="Scheduled" value={f.scheduling.length} color="#FCD34D" sub={`${f.scheduling.filter(r=>r.campaign_status==='Active').length} active`}/>
              <Card icon="◆" title="Winners" value={f.winning.length} color="#F59E0B"/>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
              <SectionCard title="Funnel Status">
                <StatusBreakdown rows={f.funnel} statusKey="funnel_status" colors={FUNNEL_COLORS}/>
              </SectionCard>
              <SectionCard title="Creative Status">
                <StatusBreakdown rows={f.creatives} statusKey="creative_status" colors={CREATIVE_COLORS}/>
              </SectionCard>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <SectionCard title="Ad Copy Status">
                <StatusBreakdown rows={f.adcopy} statusKey="status" colors={ADCOPY_COLORS}/>
              </SectionCard>
              <SectionCard title="Campaign Status">
                <StatusBreakdown rows={f.scheduling} statusKey="campaign_status" colors={SCHED_COLORS}/>
              </SectionCard>
            </div>
          </div>
        )}

        {/* FUNNEL */}
        {activeTab==='funnel' && (
          <div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))',gap:10,marginBottom:16}}>
              <Card icon="⬡" title="Total" value={f.funnel.length} color="var(--accent)"/>
              <Card icon="✅" title="Ready" value={f.funnel.filter(r=>r.funnel_status==='Ready').length} color="var(--accent3)"/>
              <Card icon="⏳" title="In Progress" value={f.funnel.filter(r=>r.funnel_status==='In Progress').length} color="#60A5FA"/>
              <Card icon="☑" title="Fully Checked" value={f.funnel.filter(r=>{try{const cl=typeof r.checklist==='string'?JSON.parse(r.checklist||'{}'):r.checklist||{};return Object.values(cl).filter(Boolean).length>=16}catch{return false}}).length} color="var(--gold)"/>
              <Card icon="🔍" title="Audited" value={f.funnel.filter(r=>r.audit_report?.trim()).length} color="var(--purple)"/>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <SectionCard title="Status Breakdown">
                <StatusBreakdown rows={f.funnel} statusKey="funnel_status" colors={FUNNEL_COLORS}/>
              </SectionCard>
              <SectionCard title="By Market">
                <ByGroup rows={f.funnel} groupKey="market"/>
              </SectionCard>
              <SectionCard title="Checklist Completion">
                {(() => {
                  const buckets = {'0%':0,'1-25%':0,'26-50%':0,'51-75%':0,'76-99%':0,'100%':0}
                  f.funnel.forEach(r=>{
                    try{const cl=typeof r.checklist==='string'?JSON.parse(r.checklist||'{}'):r.checklist||{};const pct=Math.round(Object.values(cl).filter(Boolean).length/16*100);if(pct===0)buckets['0%']++;else if(pct<=25)buckets['1-25%']++;else if(pct<=50)buckets['26-50%']++;else if(pct<=75)buckets['51-75%']++;else if(pct<100)buckets['76-99%']++;else buckets['100%']++;}catch{}
                  })
                  const pieData = Object.entries(buckets).filter(([,v])=>v>0).map(([label,value],i)=>({label,value,color:['#64748B','#F59E0B','#3B82F6','#8B5CF6','#06B6D4','#10B981'][i]}))
                  return <StatusBreakdown rows={f.funnel.map((_,i)=>({fake:Object.keys(buckets)[i]}))} statusKey="fake" colors={{}} />
                })()}
                <div style={{display:'flex',flexDirection:'column',gap:5,marginTop:8}}>
                  {f.funnel.slice(0,5).map(r=>{
                    let pct=0;try{const cl=typeof r.checklist==='string'?JSON.parse(r.checklist||'{}'):r.checklist||{};pct=Math.round(Object.values(cl).filter(Boolean).length/16*100)}catch{}
                    return(
                      <div key={r.id} style={{display:'flex',alignItems:'center',gap:8}}>
                        <div style={{flex:1,fontSize:11,color:'var(--text2)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.product_name} ({r.market})</div>
                        <div style={{width:80,height:5,background:'var(--border)',borderRadius:2,overflow:'hidden'}}>
                          <div style={{height:'100%',width:`${pct}%`,background:pct===100?'var(--accent3)':'var(--accent)',borderRadius:2}}/>
                        </div>
                        <div style={{fontSize:10,color:'var(--text3)',fontFamily:'DM Mono,monospace',width:30,textAlign:'right'}}>{pct}%</div>
                      </div>
                    )
                  })}
                </div>
              </SectionCard>
              <SectionCard title="By Creator">
                <ByGroup rows={f.funnel} groupKey="who_creates_funnel"/>
              </SectionCard>
            </div>
          </div>
        )}

        {/* CREATIVES */}
        {activeTab==='creatives' && (
          <div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))',gap:10,marginBottom:16}}>
              <Card icon="◐" title="Total" value={f.creatives.length} color="var(--accent)"/>
              <Card icon="✅" title="Ready" value={f.creatives.filter(r=>r.creative_status==='Ready').length} color="var(--accent3)"/>
              <Card icon="⏳" title="In Progress" value={f.creatives.filter(r=>r.creative_status==='In Progress').length} color="#60A5FA"/>
              <Card icon="⏸" title="On Hold" value={f.creatives.filter(r=>r.creative_status==='On Hold').length} color="var(--gold)"/>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <SectionCard title="Status Breakdown">
                <StatusBreakdown rows={f.creatives} statusKey="creative_status" colors={CREATIVE_COLORS}/>
              </SectionCard>
              <SectionCard title="By Market">
                <ByGroup rows={f.creatives} groupKey="market"/>
              </SectionCard>
              <SectionCard title="By Editor">
                <ByGroup rows={f.creatives} groupKey="assigned_editor"/>
              </SectionCard>
              <SectionCard title="By Product">
                <ByGroup rows={f.creatives} groupKey="product_name"/>
              </SectionCard>
            </div>
          </div>
        )}

        {/* AD COPY */}
        {activeTab==='adcopy' && (
          <div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))',gap:10,marginBottom:16}}>
              <Card icon="◻" title="Total" value={f.adcopy.length} color="var(--accent)"/>
              <Card icon="✅" title="Approved" value={f.adcopy.filter(r=>r.status==='Approved').length} color="var(--accent3)"/>
              <Card icon="👁" title="In Review" value={f.adcopy.filter(r=>r.status==='Review').length} color="#60A5FA"/>
              <Card icon="✨" title="Has AI Copy" value={f.adcopy.filter(r=>r.ai_copy_primary?.trim()).length} color="var(--purple)"/>
              <Card icon="🔄" title="Has Adapted" value={f.adcopy.filter(r=>r.adapted_copy_primary?.trim()).length} color="var(--gold)"/>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <SectionCard title="Status Breakdown">
                <StatusBreakdown rows={f.adcopy} statusKey="status" colors={ADCOPY_COLORS}/>
              </SectionCard>
              <SectionCard title="By Market">
                <ByGroup rows={f.adcopy} groupKey="market"/>
              </SectionCard>
              <SectionCard title="By Approver">
                <ByGroup rows={f.adcopy} groupKey="approved_by"/>
              </SectionCard>
              <SectionCard title="By Product">
                <ByGroup rows={f.adcopy} groupKey="product_name"/>
              </SectionCard>
            </div>
          </div>
        )}

        {/* SCHEDULING */}
        {activeTab==='scheduling' && (
          <div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))',gap:10,marginBottom:16}}>
              <Card icon="◷" title="Total" value={f.scheduling.length} color="var(--accent)"/>
              <Card icon="🟢" title="Active" value={f.scheduling.filter(r=>r.campaign_status==='Active').length} color="var(--accent3)"/>
              <Card icon="📅" title="Scheduled" value={f.scheduling.filter(r=>r.campaign_status==='Scheduled').length} color="#60A5FA"/>
              <Card icon="⏸" title="Paused" value={f.scheduling.filter(r=>r.campaign_status==='Paused').length} color="var(--gold)"/>
              <Card icon="✅" title="Completed" value={f.scheduling.filter(r=>r.campaign_status==='Completed').length} color="var(--purple)"/>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <SectionCard title="Campaign Status">
                <StatusBreakdown rows={f.scheduling} statusKey="campaign_status" colors={SCHED_COLORS}/>
              </SectionCard>
              <SectionCard title="By Market">
                <ByGroup rows={f.scheduling} groupKey="market"/>
              </SectionCard>
              <SectionCard title="By Ad Account">
                <ByGroup rows={f.scheduling} groupKey="ad_account"/>
              </SectionCard>
              <SectionCard title="Upcoming (next 30 days)">
                {(() => {
                  const now = new Date(), future = new Date(); future.setDate(future.getDate()+30)
                  const upcoming = f.scheduling.filter(r=>r.scheduled_date&&new Date(r.scheduled_date)>=now&&new Date(r.scheduled_date)<=future).sort((a,b)=>new Date(a.scheduled_date)-new Date(b.scheduled_date))
                  return upcoming.length===0 ? <div style={{fontSize:12,color:'var(--text3)'}}>No upcoming schedules</div> : (
                    <div style={{display:'flex',flexDirection:'column',gap:6}}>
                      {upcoming.slice(0,8).map(r=>(
                        <div key={r.id} style={{display:'flex',gap:10,alignItems:'center'}}>
                          <div style={{fontSize:10,color:'var(--accent)',fontFamily:'DM Mono,monospace',flexShrink:0,width:80}}>{r.scheduled_date?.split('T')[0]}</div>
                          <div style={{flex:1,fontSize:11,color:'var(--text2)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.product_name}</div>
                          <div style={{fontSize:10,color:'var(--text3)',fontFamily:'DM Mono,monospace'}}>{r.market}</div>
                        </div>
                      ))}
                    </div>
                  )
                })()}
              </SectionCard>
            </div>
          </div>
        )}

        {/* WINNING */}
        {activeTab==='winning' && (
          <div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))',gap:10,marginBottom:16}}>
              <Card icon="◆" title="Total Winners" value={f.winning.length} color="var(--gold)"/>
              <Card icon="📈" title="Avg BE ROAS" value={f.winning.length?`${(f.winning.reduce((s,r)=>s+(parseFloat(r.be_roas)||0),0)/f.winning.length).toFixed(2)}x`:'—'} color="var(--purple)"/>
              <Card icon="💰" title="Avg Profit" value={f.winning.length?`€${(f.winning.reduce((s,r)=>s+(parseFloat(r.profit)||0),0)/f.winning.length).toFixed(2)}`:'—'} color="var(--accent3)"/>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <SectionCard title="By Market">
                <ByGroup rows={f.winning} groupKey="market"/>
              </SectionCard>
              <SectionCard title="By Product">
                <ByGroup rows={f.winning} groupKey="product_name"/>
              </SectionCard>
              <SectionCard title="Top Performers (by ROAS)">
                <div style={{display:'flex',flexDirection:'column',gap:6}}>
                  {f.winning.filter(r=>r.be_roas).sort((a,b)=>(parseFloat(b.be_roas)||0)-(parseFloat(a.be_roas)||0)).slice(0,8).map(r=>(
                    <div key={r.id} style={{display:'flex',gap:10,alignItems:'center'}}>
                      <div style={{flex:1,fontSize:11,color:'var(--text2)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.product_name}</div>
                      <div style={{fontSize:10,color:'var(--text3)',fontFamily:'DM Mono,monospace'}}>{r.market}</div>
                      <div style={{fontSize:11,fontWeight:700,color:'var(--purple)',fontFamily:'DM Mono,monospace',width:40,textAlign:'right'}}>{r.be_roas}x</div>
                      <div style={{fontSize:11,fontWeight:700,color:'var(--accent3)',fontFamily:'DM Mono,monospace',width:55,textAlign:'right'}}>€{parseFloat(r.profit||0).toFixed(2)}</div>
                    </div>
                  ))}
                  {f.winning.filter(r=>r.be_roas).length===0 && <div style={{fontSize:12,color:'var(--text3)'}}>No winning products with ROAS data</div>}
                </div>
              </SectionCard>
              <SectionCard title="By Ad Account">
                <ByGroup rows={f.winning} groupKey="ad_account"/>
              </SectionCard>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
